import { jsonify, sanitize } from "../TapiocaUtils";
export default class ProjectTemplatesClient {
  constructor(raw) {
    this.raw = raw;
  }
  cloneCompletionCallback(body) {
    const url = "/v1/projects/clone_completion_callback";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Fetches a paginated list of pending users on a template
   */
  getPendingUsers(templateId, params) {
    const url = {
      url: `/v1/templates/${templateId}/users`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * Get the project template UID and name associated with a given project UID
   */
  getSourceTemplate(projectId) {
    const url = `/v1/templates/project/${projectId}/template`;
    return this.raw.get(url);
  }
  /**
   * Get a template at the account level.
   */
  getTemplate(templateId) {
    const url = `/v1/templates/${templateId}`;
    return this.raw.get(url);
  }
  /**
   * Obtain a paginated list of templates + published status in a given account.
   */
  listAccountTemplates(accountId, params) {
    const url = {
      url: `/v1/templates/account/${accountId}/templates`,
      params: sanitize(params || {}, {
        "filter[isPublished]": "csv",
        "filter[templateId]": "csv",
      }),
    };
    return this.raw.get(url);
  }
  /**
   * Obtain a list of projects created from the given template. Can return None if
   * the template UID is valid, but no projects have been created from it.
   */
  listCreatedProjects(templateId, params) {
    const url = {
      url: `/v1/templates/${templateId}/projects`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * List the # of projects created from each template, given a list of template ids.
   */
  listProjectCreationCount(accountId, params) {
    const url = {
      url: `/v1/templates/account/${accountId}/project-creation-count`,
      params: sanitize(params || {}, { "filter[templateId]": "csv" }),
    };
    return this.raw.get(url);
  }
  /**
   * Obtain a list of template IDs and names associated with the given
   * list of project IDs. Values may be None if the project was not created from
   * a template.
   * For workflows requiring processing of more than 50 projects see the related endpoint:
   * /templates:template-info
   */
  listTemplateInfoForProjects(accountId, params) {
    const url = {
      url: `/v1/templates/account/${accountId}/template-info`,
      params: sanitize(params || {}, { "filter[projectId]": "csv" }),
    };
    return this.raw.get(url);
  }
  /**
   * Obtain a list of template IDs and names associated with the given
   * list of project IDs. Values may be None if the project was not created from
   * a template. This endpoint is intended for use with large numbers of project IDs.
   * For workflows processing of less than 50 projects consider the related endpoint:
   * /templates/account/<uuid:accountId>/template-info
   */
  listTemplateInfoForProjectsCustom(body) {
    const url = "/v1/templates:template-info";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Deletes a single pending user.
   */
  pendingUserDelete(templateId, pendingUserUid) {
    const url = `/v1/templates/${templateId}/users/${pendingUserUid}`;
    return this.raw.delete(url);
  }
  /**
   * Edit the pending user on a template with the provided information in the request body.
   * Leaving a field blank in the request body will null the field. In the case of `products`,
   * an null field will remove access to all products
   */
  pendingUserEdit(templateId, pendingUserUid, body) {
    const url = `/v1/templates/${templateId}/users/${pendingUserUid}`;
    return this.raw.patch(url, jsonify(body));
  }
  /**
   * Create a list of pending users.
   */
  pendingUsersCreate(templateId, body) {
    const url = `/v1/templates/${templateId}/users`;
    return this.raw.post(url, jsonify(body));
  }
  systemInfo() {
    const url = "/v1/system/info";
    return this.raw.get(url);
  }
  /**
   * This endpoint implements the "create project container" contract used by Mission Control when
   * a project is created.
   * if a source project id is not provided, no action is taken.
   * If a source project id is provided, we call PADD to create a clone of type 'template'.
   * if source or target projects not found, returns 404.
   * if source and target project classifications are invalid, returns 409.
   */
  templateCreate(body) {
    const url = "/v1/projects";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * This endpoint implements the interface for deleting PII as outlined by Mission Control.
   * No PII is stored by this service so a noop is performed.
   */
  templateDelete(targetProjectUid) {
    const url = `/v1/projects/${targetProjectUid}`;
    return this.raw.delete(url);
  }
  /**
   * Publish a template at the account level. Requires account admin level permissions.
   */
  templatePublish(templateId) {
    const url = `/v1/templates/${templateId}:publish`;
    return this.raw.post(url, {});
  }
  /**
   * Unpublish a template at the account level. Requires account admin level permissions.
   */
  templateUnpublish(templateId) {
    const url = `/v1/templates/${templateId}:unpublish`;
    return this.raw.post(url, {});
  }
  /**
   * Validate usage of a template at the account level.
   */
  validateTemplate(params) {
    const url = {
      url: "/v1/template:validateUsage",
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
}
